import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { devices, measurements, venues, violations as violationsApi, thresholds as thresholdsApi } from '../services/api';
import ThresholdModal from './ThresholdModal';
import {
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  SpeakerWaveIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { format, subHours, subDays, startOfDay, endOfDay } from 'date-fns';

interface Device {
  id: string;
  device_name: string;
  venue_id: string;
  venue_name: string;
  location?: string;
  status: string;
  is_online: boolean;
  last_seen: string;
}

interface Measurement {
  timestamp: string;
  leq_1min: number;
  leq_5min: number;
  leq_15min: number;
  lmax: number;
  lpeak: number;
  l10: number;
  l50: number;
  l90: number;
}

interface Threshold {
  id: string;
  name: string;
  max_laeq_15min: number;
  max_lc_peak: number;
  start_hour: number;
  end_hour: number;
  days_of_week?: number[];
  enabled: boolean;
}

interface Violation {
  id: string;
  timestamp?: string;
  time?: string;
  device_id: string;
  threshold_name: string;
  measured_value: number;
  threshold_value: number;
  metric_type: string;
  duration_seconds: number;
}

interface ComplianceStats {
  compliance_percentage: number;
  total_violations: number;
  violations_today: number;
  violations_this_week: number;
  average_leq: number;
  peak_level: number;
  quiet_hours_compliance: number;
}

type TabType = 'live' | 'historical' | 'compliance' | 'violations' | 'thresholds' | 'reports';
type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';

const ComprehensiveMonitoring: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState<TabType>('live');
  const [selectedVenue, setSelectedVenue] = useState<string>(venueId || '');
  const [venuesList, setVenuesList] = useState<any[]>([]);
  const [devicesList, setDevicesList] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [liveData, setLiveData] = useState<Measurement[]>([]);
  const [historicalData, setHistoricalData] = useState<Measurement[]>([]);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [complianceStats, setComplianceStats] = useState<ComplianceStats | null>(null);
  
  // Modal states
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState<Threshold | null>(null);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  // Load venues on mount
  useEffect(() => {
    if (user?.role === 'engineer' || user?.role === 'admin') {
      loadVenues();
    }
  }, [user]);

  // Load devices when venue changes
  useEffect(() => {
    if (selectedVenue) {
      loadDevices();
    }
  }, [selectedVenue]);

  // Load data when device or time range changes
  useEffect(() => {
    if (selectedDevice) {
      loadMonitoringData();
    }
  }, [selectedDevice, timeRange, activeTab]);

  // Auto-refresh live data
  useEffect(() => {
    if (activeTab === 'live' && selectedDevice) {
      const interval = setInterval(() => {
        loadLiveData();
      }, 5000); // Refresh every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [activeTab, selectedDevice]);

  const loadVenues = async () => {
    try {
      const response = await venues.list();
      setVenuesList(response.data);
      if (!selectedVenue && response.data.length > 0) {
        setSelectedVenue(response.data[0].id);
      }
    } catch (err) {
      console.error('Error loading venues:', err);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await devices.list(selectedVenue);
      setDevicesList(response.data);
      if (response.data.length > 0 && !selectedDevice) {
        setSelectedDevice(response.data[0].id);
      } else if (response.data.length === 0) {
        // No devices found - set loading to false
        setLoading(false);
        setError('No devices found for this venue. Please add a device first.');
      }
    } catch (err) {
      console.error('Error loading devices:', err);
      setLoading(false);
      setError('Failed to load devices');
    }
  };

  const loadMonitoringData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      switch (activeTab) {
        case 'live':
          await loadLiveData();
          break;
        case 'historical':
          await loadHistoricalData();
          break;
        case 'compliance':
          await loadComplianceData();
          break;
        case 'violations':
          await loadViolations();
          break;
        case 'thresholds':
          await loadThresholds();
          break;
        default:
          break;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
      console.error('Error loading monitoring data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadLiveData = async () => {
    try {
      const response = await measurements.getLatest(selectedDevice, 60);
      setLiveData(response.data || []);
    } catch (err) {
      console.error('Error loading live data:', err);
    }
  };

  const loadHistoricalData = async () => {
    try {
      // For now, use the getLatest with appropriate limit based on time range
      let limit = 100;
      switch (timeRange) {
        case '1h': limit = 60; break;
        case '6h': limit = 360; break;
        case '24h': limit = 1440; break;
        case '7d': limit = 10080; break;
        case '30d': limit = 43200; break;
      }
      
      const response = await measurements.getLatest(selectedDevice, limit);
      setHistoricalData(response.data || []);
    } catch (err) {
      console.error('Error loading historical data:', err);
    }
  };

  const loadComplianceData = async () => {
    try {
      // For now, use stub data until backend endpoints are ready
      setComplianceStats({
        compliance_percentage: 95.5,
        total_violations: 12,
        violations_today: 2,
        violations_this_week: 5,
        average_leq: 82.3,
        peak_level: 105.2,
        quiet_hours_compliance: 98.2
      });
      
      setThresholds([
        {
          id: '1',
          name: 'Daytime',
          max_laeq_15min: 95,
          max_lc_peak: 115,
          start_hour: 7,
          end_hour: 23,
          enabled: true
        },
        {
          id: '2',
          name: 'Nighttime',
          max_laeq_15min: 85,
          max_lc_peak: 105,
          start_hour: 23,
          end_hour: 7,
          enabled: true
        }
      ]);
    } catch (err) {
      console.error('Error loading compliance data:', err);
    }
  };

  const loadViolations = async () => {
    try {
      // For now, use the violations endpoint if available
      const response = await violationsApi.list({ device_id: selectedDevice });
      setViolations(response.data || []);
    } catch (err) {
      console.error('Error loading violations:', err);
      setViolations([]);
    }
  };

  const loadThresholds = async () => {
    try {
      const response = await thresholdsApi.list(selectedVenue);
      setThresholds(response.data || []);
    } catch (err) {
      console.error('Error loading thresholds:', err);
      // If API fails, use stub data
      setThresholds([
        {
          id: '1',
          name: 'Daytime Limit',
          max_laeq_15min: 85,
          max_lc_peak: 115,
          start_hour: 7,
          end_hour: 22,
          days_of_week: [1, 2, 3, 4, 5],
          enabled: true
        },
        {
          id: '2',
          name: 'Night Time Limit',
          max_laeq_15min: 70,
          max_lc_peak: 100,
          start_hour: 22,
          end_hour: 7,
          days_of_week: [0, 1, 2, 3, 4, 5, 6],
          enabled: true
        }
      ]);
    }
  };

  const handleAddThreshold = () => {
    setEditingThreshold(null);
    setModalMode('add');
    setThresholdModalOpen(true);
  };

  const handleEditThreshold = (threshold: Threshold) => {
    setEditingThreshold(threshold);
    setModalMode('edit');
    setThresholdModalOpen(true);
  };

  const handleSaveThreshold = async (data: any) => {
    try {
      if (modalMode === 'add') {
        await thresholdsApi.create({
          ...data,
          venue_id: selectedVenue
        });
      } else if (editingThreshold) {
        await thresholdsApi.update(editingThreshold.id, data);
      }
      await loadThresholds();
      setThresholdModalOpen(false);
    } catch (err) {
      console.error('Error saving threshold:', err);
      throw err;
    }
  };

  const handleDeleteThreshold = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this threshold?')) {
      try {
        await thresholdsApi.delete(id);
        await loadThresholds();
      } catch (err) {
        console.error('Error deleting threshold:', err);
        alert('Failed to delete threshold');
      }
    }
  };

  const handleToggleThreshold = async (threshold: Threshold) => {
    const newState = !threshold.enabled;
    const confirmMessage = newState
      ? `Enable "${threshold.name}" threshold? Violations will be tracked when this limit is exceeded.`
      : `Disable "${threshold.name}" threshold? No violations will be tracked for this limit.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      await thresholdsApi.update(threshold.id, { enabled: newState });
      await loadThresholds();
    } catch (err) {
      console.error('Error toggling threshold:', err);
      alert('Failed to toggle threshold');
    }
  };

  const handleExportData = async (format: 'csv' | 'pdf' | 'json') => {
    try {
      const response = await measurements.exportData(
        selectedDevice,
        timeRange,
        format
      );
      
      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : format === 'pdf' ? 'application/pdf' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spl-report-${format}-${Date.now()}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const renderLiveView = () => {
    const latestMeasurement = liveData[liveData.length - 1];
    
    return (
      <div className="space-y-6">
        {/* Current Levels Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">LAeq (1min)</p>
                <p className="text-2xl font-bold">
                  {latestMeasurement?.leq_1min?.toFixed(1) || '--'} dB
                </p>
              </div>
              <SpeakerWaveIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">LAeq (15min)</p>
                <p className="text-2xl font-bold">
                  {latestMeasurement?.leq_15min?.toFixed(1) || '--'} dB
                </p>
              </div>
              <ChartBarIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">LCpeak</p>
                <p className="text-2xl font-bold">
                  {latestMeasurement?.lpeak?.toFixed(1) || '--'} dB
                </p>
              </div>
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">L10</p>
                <p className="text-2xl font-bold">
                  {latestMeasurement?.l10?.toFixed(1) || '--'} dB
                </p>
              </div>
              <ArrowTrendingUpIcon className="h-8 w-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">L90</p>
                <p className="text-2xl font-bold">
                  {latestMeasurement?.l90?.toFixed(1) || '--'} dB
                </p>
              </div>
              <ArrowTrendingUpIcon className="h-8 w-8 text-indigo-500" />
            </div>
          </div>
        </div>

        {/* Live Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Real-time SPL Levels</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={liveData.slice(-60)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(ts) => format(new Date(ts), 'HH:mm:ss')}
              />
              <YAxis domain={[40, 120]} />
              <Tooltip 
                labelFormatter={(ts) => format(new Date(ts), 'HH:mm:ss')}
                formatter={(value: any) => `${value?.toFixed(1)} dB`}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="leq_1min" 
                stroke="#3B82F6" 
                name="LAeq 1min"
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="leq_15min" 
                stroke="#10B981" 
                name="LAeq 15min"
                strokeWidth={2}
                dot={false}
              />
              <Line 
                type="monotone" 
                dataKey="lpeak" 
                stroke="#EF4444" 
                name="LCpeak"
                strokeWidth={1}
                strokeDasharray="5 5"
                dot={false}
              />
              {/* Add threshold lines */}
              {thresholds.filter(t => t.enabled).map(threshold => (
                <ReferenceLine 
                  key={threshold.id}
                  y={threshold.max_laeq_15min} 
                  stroke="#FFA500" 
                  strokeDasharray="3 3"
                  label={`${threshold.name} Limit`}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderHistoricalView = () => {
    return (
      <div className="space-y-6">
        {/* Time Range Selector */}
        <div className="flex space-x-2">
          {(['1h', '6h', '24h', '7d', '30d'] as TimeRange[]).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {range}
            </button>
          ))}
        </div>

        {/* Historical Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LAeq Trends */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">LAeq Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(ts) => format(new Date(ts), 
                    timeRange === '1h' || timeRange === '6h' ? 'HH:mm' : 'MM/dd HH:mm'
                  )}
                />
                <YAxis domain={[40, 100]} />
                <Tooltip 
                  labelFormatter={(ts) => format(new Date(ts), 'yyyy-MM-dd HH:mm')}
                  formatter={(value: any) => `${value?.toFixed(1)} dB`}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="leq_15min" 
                  stroke="#10B981" 
                  fill="#10B981"
                  fillOpacity={0.3}
                  name="LAeq 15min"
                />
                <Area 
                  type="monotone" 
                  dataKey="leq_5min" 
                  stroke="#3B82F6" 
                  fill="#3B82F6"
                  fillOpacity={0.3}
                  name="LAeq 5min"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Peak Levels */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Peak Levels</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(ts) => format(new Date(ts), 
                    timeRange === '1h' || timeRange === '6h' ? 'HH:mm' : 'MM/dd HH:mm'
                  )}
                />
                <YAxis domain={[50, 120]} />
                <Tooltip 
                  labelFormatter={(ts) => format(new Date(ts), 'yyyy-MM-dd HH:mm')}
                  formatter={(value: any) => `${value?.toFixed(1)} dB`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="lpeak" 
                  stroke="#EF4444" 
                  name="LCpeak"
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="lmax" 
                  stroke="#F59E0B" 
                  name="LAmax"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Statistical Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Statistical Levels</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={historicalData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(ts) => format(new Date(ts), 
                    timeRange === '1h' || timeRange === '6h' ? 'HH:mm' : 'MM/dd HH:mm'
                  )}
                />
                <YAxis domain={[40, 100]} />
                <Tooltip 
                  labelFormatter={(ts) => format(new Date(ts), 'yyyy-MM-dd HH:mm')}
                  formatter={(value: any) => `${value?.toFixed(1)} dB`}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="l90" 
                  stroke="#6366F1" 
                  fill="#6366F1"
                  fillOpacity={0.6}
                  name="L90 (Background)"
                />
                <Area 
                  type="monotone" 
                  dataKey="l50" 
                  stroke="#8B5CF6" 
                  fill="#8B5CF6"
                  fillOpacity={0.4}
                  name="L50 (Median)"
                />
                <Area 
                  type="monotone" 
                  dataKey="l10" 
                  stroke="#EC4899" 
                  fill="#EC4899"
                  fillOpacity={0.2}
                  name="L10 (Peaks)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Hourly Averages */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Hourly Averages</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={historicalData.filter((_, i) => i % 12 === 0)} // Sample every hour
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(ts) => format(new Date(ts), 'HH:mm')}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip 
                  labelFormatter={(ts) => format(new Date(ts), 'yyyy-MM-dd HH:mm')}
                  formatter={(value: any) => `${value?.toFixed(1)} dB`}
                />
                <Legend />
                <Bar dataKey="leq_15min" fill="#10B981" name="LAeq 15min" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderComplianceView = () => {
    if (!complianceStats) return null;
    
    return (
      <div className="space-y-6">
        {/* Compliance Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Compliance Rate</p>
                <p className="text-3xl font-bold">
                  {complianceStats.compliance_percentage.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Last {timeRange}</p>
              </div>
              {complianceStats.compliance_percentage >= 95 ? (
                <CheckCircleIcon className="h-10 w-10 text-green-500" />
              ) : (
                <ExclamationTriangleIcon className="h-10 w-10 text-yellow-500" />
              )}
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Violations</p>
                <p className="text-3xl font-bold">{complianceStats.total_violations}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Today: {complianceStats.violations_today}
                </p>
              </div>
              <ExclamationTriangleIcon className="h-10 w-10 text-red-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Average LAeq</p>
                <p className="text-3xl font-bold">
                  {complianceStats.average_leq.toFixed(1)} dB
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">15-min average</p>
              </div>
              <ChartBarIcon className="h-10 w-10 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Peak Level</p>
                <p className="text-3xl font-bold">
                  {complianceStats.peak_level.toFixed(1)} dB
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Maximum LCpeak</p>
              </div>
              <ArrowTrendingUpIcon className="h-10 w-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Active Thresholds */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Active Thresholds</h3>
          <div className="space-y-3">
            {thresholds.filter(t => t.enabled).map(threshold => (
              <div key={threshold.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">{threshold.name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {threshold.start_hour}:00 - {threshold.end_hour}:00
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">
                      LAeq(15min): <span className="font-semibold">{threshold.max_laeq_15min} dB</span>
                    </p>
                    <p className="text-sm">
                      LCpeak: <span className="font-semibold">{threshold.max_lc_peak} dB</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Trends Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Compliance Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(ts) => format(new Date(ts), 'MM/dd')}
              />
              <YAxis yAxisId="left" domain={[0, 100]} />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                labelFormatter={(ts) => format(new Date(ts), 'yyyy-MM-dd')}
              />
              <Legend />
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="compliance_rate" 
                stroke="#10B981" 
                fill="#10B981"
                fillOpacity={0.3}
                name="Compliance %"
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="violations_count" 
                stroke="#EF4444" 
                name="Violations"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderViolationsView = () => {
    return (
      <div className="space-y-6">
        {/* Violations Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold">Violation History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Threshold
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Metric
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Measured
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Limit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Excess
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {violations.map((violation) => {
                  const excess = violation.measured_value - violation.threshold_value;
                  return (
                    <tr key={violation.id} className="hover:bg-gray-50 dark:bg-gray-900">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {(() => {
                          const dateStr = violation.timestamp || violation.time;
                          if (!dateStr) return 'N/A';
                          try {
                            return format(new Date(dateStr), 'yyyy-MM-dd HH:mm:ss');
                          } catch (e) {
                            console.error('Invalid date:', dateStr);
                            return 'Invalid Date';
                          }
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {violation.threshold_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {violation.metric_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                        {violation.measured_value.toFixed(1)} dB
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {violation.threshold_value.toFixed(1)} dB
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`font-medium ${
                          excess > 5 ? 'text-red-600' : 
                          excess > 3 ? 'text-yellow-600' : 
                          'text-orange-600'
                        }`}>
                          +{excess.toFixed(1)} dB
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {violation.duration_seconds > 60 
                          ? `${Math.floor(violation.duration_seconds / 60)}m ${violation.duration_seconds % 60}s`
                          : `${violation.duration_seconds}s`
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {violations.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No violations recorded in the selected time period
              </div>
            )}
          </div>
        </div>

        {/* Violations by Hour Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Violations by Hour of Day</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={Array.from({ length: 24 }, (_, hour) => ({
                hour: `${hour}:00`,
                count: violations.filter(v => {
                  const dateStr = v.timestamp || v.time;
                  if (!dateStr) return false;
                  try {
                    return new Date(dateStr).getHours() === hour;
                  } catch {
                    return false;
                  }
                }).length
              }))}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#EF4444" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderThresholdsView = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold">Threshold Configuration</h3>
            <button 
              onClick={handleAddThreshold}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add Threshold
            </button>
          </div>
          <div className="p-6">
            {thresholds.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No thresholds configured yet.</p>
                <button 
                  onClick={handleAddThreshold}
                  className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-800"
                >
                  Add your first threshold
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {thresholds.map(threshold => (
                  <div key={threshold.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="font-medium text-lg">{threshold.name}</h4>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            threshold.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {threshold.enabled ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Time Period</p>
                            <p className="font-medium">
                              {threshold.start_hour}:00 - {threshold.end_hour}:00
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">LAeq (15min) Limit</p>
                            <p className="font-medium">{threshold.max_laeq_15min} dB</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">LCpeak Limit</p>
                            <p className="font-medium">{threshold.max_lc_peak} dB</p>
                          </div>
                          {threshold.days_of_week && threshold.days_of_week.length < 7 && (
                            <div>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Active Days</p>
                              <p className="font-medium">
                                {threshold.days_of_week.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col items-center">
                          <button
                            onClick={() => handleToggleThreshold(threshold)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                              threshold.enabled
                                ? 'bg-green-600'
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}
                            title={threshold.enabled ? 'Disable threshold' : 'Enable threshold'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                threshold.enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {threshold.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <button
                          onClick={() => handleEditThreshold(threshold)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800"
                          title="Edit threshold"
                        >
                          <CogIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteThreshold(threshold.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete threshold"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderReportsView = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Generate Reports</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md">
                <option>Compliance Summary</option>
                <option>Detailed Measurements</option>
                <option>Violation Report</option>
                <option>Statistical Analysis</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Period
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md">
                <option>Last 24 Hours</option>
                <option>Last 7 Days</option>
                <option>Last 30 Days</option>
                <option>Custom Range</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Export Format
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => handleExportData('pdf')}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 inline mr-2" />
                  PDF
                </button>
                <button
                  onClick={() => handleExportData('csv')}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 inline mr-2" />
                  CSV
                </button>
                <button
                  onClick={() => handleExportData('json')}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 inline mr-2" />
                  JSON
                </button>
              </div>
            </div>
            
            <button className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Generate Report
            </button>
          </div>
        </div>
        
        {/* Recent Reports */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Reports</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center p-3 border rounded hover:bg-gray-50 dark:bg-gray-900">
              <div>
                <p className="font-medium">Monthly Compliance Report</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Generated on 2024-01-15</p>
              </div>
              <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800">
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex justify-between items-center p-3 border rounded hover:bg-gray-50 dark:bg-gray-900">
              <div>
                <p className="font-medium">Weekly Violation Summary</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Generated on 2024-01-10</p>
              </div>
              <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800">
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Comprehensive SPL Monitoring
        </h1>
        
        {/* Venue and Device Selectors */}
        <div className="flex space-x-4 mb-6">
          {(user?.role === 'engineer' || user?.role === 'admin') && (
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select Venue</option>
              {venuesList.map(venue => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
          )}

          <select
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select Device</option>
            {devicesList.map(device => (
              <option key={device.id} value={device.id}>
                {device.device_name} {device.location && `(${device.location})`}
              </option>
            ))}
          </select>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'live', label: 'Live Monitoring', icon: SpeakerWaveIcon },
              { id: 'historical', label: 'Historical Data', icon: ClockIcon },
              { id: 'compliance', label: 'Compliance', icon: CheckCircleIcon },
              { id: 'violations', label: 'Violations', icon: ExclamationTriangleIcon },
              { id: 'thresholds', label: 'Thresholds', icon: CogIcon },
              { id: 'reports', label: 'Reports', icon: ArrowDownTrayIcon }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : !selectedDevice ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a device to view monitoring data</p>
        </div>
      ) : (
        <>
          {activeTab === 'live' && renderLiveView()}
          {activeTab === 'historical' && renderHistoricalView()}
          {activeTab === 'compliance' && renderComplianceView()}
          {activeTab === 'violations' && renderViolationsView()}
          {activeTab === 'thresholds' && renderThresholdsView()}
          {activeTab === 'reports' && renderReportsView()}
        </>
      )}
      
      {/* Threshold Modal */}
      <ThresholdModal
        isOpen={thresholdModalOpen}
        onClose={() => setThresholdModalOpen(false)}
        onSave={handleSaveThreshold}
        threshold={editingThreshold}
        mode={modalMode}
      />
    </div>
  );
};

export default ComprehensiveMonitoring;